import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Lakara, Pada } from '@/sanskrit/lib/sanskrit/types'

import Link from 'next/link'
import { useState } from 'react'
import { ChevronDown, FlaskConical } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { SanskritGate } from '@/components/plans/sanskrit-gate'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { ALL_DHATUS, getDhatu, padasOf } from '@/sanskrit/lib/sanskrit/dhatus'
import { availableLakaras, getParadigm } from '@/sanskrit/lib/sanskrit/paradigms'
import { KRIDANTA_FORMS, KRIDANTA_PRATYAYAS, UPASARGA_EXAMPLES } from '@/sanskrit/lib/sanskrit/derivation'
import { SOURCES } from '@/sanskrit/lib/sanskrit/sources'
import { GANA_LABEL, LAKARA_LABEL, PADA_CLASS_LABEL, PADA_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { Chip, Fact, StatusBadge } from '@/sanskrit/components/vani/bits'
import { VerbTableView } from '@/sanskrit/components/grammar/tables'
import AudioButton from '@/sanskrit/components/vani/audio-button'
import Skt from '@/sanskrit/components/vani/skt'

interface DhatuPageProps {
    root: string
}

function ParadigmSection({ dhatuId, lakara, pada, defaultOpen }: { dhatuId: string; lakara: Lakara; pada: Pada; defaultOpen: boolean }) {
    const t = useT()
    const [open, setOpen] = useState(defaultOpen)
    const paradigm = getParadigm(dhatuId, lakara, pada)
    if (!paradigm) return null
    const label = LAKARA_LABEL[lakara]

    return (
        <section className='rounded-3xl border border-border bg-card'>
            <button type='button' onClick={() => setOpen(o => !o)} aria-expanded={open} className='flex w-full items-center justify-between gap-3 p-4 text-left'>
                <span className='flex flex-wrap items-center gap-2'>
                    <span lang='sa' className='deva text-xl font-semibold text-ink'>{label.sa}</span>
                    <span className='text-sm text-muted-foreground'>{t({ hi: label.hi, en: label.en })}</span>
                    <span lang='sa' className='deva text-sm text-saffron-strong'>{paradigm.forms.prathama.eka[0]}</span>
                </span>
                <span className='flex items-center gap-2'>
                    <StatusBadge status={paradigm.status} />
                    <ChevronDown className={cn('size-5 transition-transform', open && 'rotate-180')} />
                </span>
            </button>
            {open && (
                <div className='space-y-3 border-t border-border p-4'>
                    {paradigm.note && <p className='rounded-xl bg-saffron-soft px-3 py-2 text-sm'>{t(paradigm.note)}</p>}
                    <p className='text-sm text-muted-foreground'>{t(label.use)}</p>
                    <VerbTableView forms={paradigm.forms} compact />
                    <Link href={`/sanskrit/grammar/dhatu?root=${dhatuId}&lakara=${lakara}&pada=${pada}`} className='inline-flex items-center gap-1.5 text-sm font-semibold text-saffron-strong'>
                        <FlaskConical className='size-4' />
                        {t({ hi: 'प्रयोगशाला में खोलें', en: 'Open in the lab' })}
                    </Link>
                </div>
            )}
        </section>
    )
}

const DhatuPage: FC<DhatuPageProps> = ({ root }): ReactNode => {
    const t = useT()
    const { settings } = useAppState()
    const d = getDhatu(root)
    if (!d) return null

    const kridantas = KRIDANTA_FORMS.filter(k => k.dhatuId === d.id)
    const prefixed = UPASARGA_EXAMPLES.filter(u => u.baseDhatuId === d.id)

    return (
        <Layout>
            <Seo title={`${d.root} (${d.meaning.en})`} description={`All verified forms of the Sanskrit root ${d.root} (${d.iast}, "${d.meaning.en}"): every lakāra, how the stem is formed, participles, prefixed verbs and examples.`} />

            <SanskritGate what='Every form of this root'>

            <div className='mx-auto max-w-5xl px-4 pt-8 pb-16 sm:px-6'>
                <Link href='/sanskrit/grammar/dhatu' className='text-sm font-semibold text-saffron-strong'>← {t({ hi: 'धातुरूप प्रयोगशाला', en: 'Dhātu lab' })}</Link>

                <header className='mt-4 flex flex-wrap items-start justify-between gap-4'>
                    <div>
                        <p className='text-sm font-semibold tracking-wide text-muted-foreground uppercase'>{t({ hi: 'धातु', en: 'Verbal root' })}</p>
                        <h1 className='flex items-center gap-3'>
                            <span lang='sa' className='literary text-6xl text-ink'>{d.root}</span>
                            <AudioButton text={d.citation} />
                        </h1>
                        <p className='iast mt-1 text-lg text-muted-foreground'>{d.iast}</p>
                        <p className='mt-2 text-xl text-foreground'>{t(d.meaning)} · <span lang='sa' className='deva font-semibold'>{d.citation}</span></p>
                    </div>
                    <div className='flex flex-wrap gap-1.5'>
                        <StatusBadge status={d.status} />
                        {d.upasarga && <Chip tone='terracotta'>{t({ hi: `उपसर्ग: ${d.upasarga}`, en: `prefix: ${d.upasarga}` })}</Chip>}
                    </div>
                </header>

                <dl className='mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-4'>
                    <Fact sa='गणः' label={{ hi: 'गण', en: 'Class' }} value={<span lang='sa' className='deva'>{GANA_LABEL[d.gana].sa}</span>} sub={`${t({ hi: GANA_LABEL[d.gana].hi, en: GANA_LABEL[d.gana].en })} · ${GANA_LABEL[d.gana].vikarana}`} />
                    <Fact sa='पदम्' label={{ hi: 'पद', en: 'Pada' }} value={<span lang='sa' className='deva'>{PADA_CLASS_LABEL[d.padaClass].sa}</span>} sub={t(PADA_CLASS_LABEL[d.padaClass])} />
                    <Fact sa='इट्' label={{ hi: 'इट् आगम', en: 'Connecting इ' }} value={<span lang='sa' className='deva'>{d.it === 'set' ? 'सेट्' : d.it === 'anit' ? 'अनिट्' : d.it === 'vet' ? 'वेट्' : '—'}</span>} sub={d.it === 'set' ? t({ hi: 'इ लेता है', en: 'takes इ' }) : d.it === 'anit' ? t({ hi: 'इ नहीं लेता', en: 'no इ' }) : undefined} />
                    {d.dhatupatha && settings.mode === 'adult' && <Fact sa='धातुपाठः' label={{ hi: 'धातुपाठ', en: 'Dhātupāṭha entry' }} value={<span lang='sa' className='deva'>{d.dhatupatha}</span>} />}
                </dl>

                {(d.presentDerivation || d.futureDerivation) && (
                    <section className='mt-8 grid gap-3 md:grid-cols-2'>
                        {d.presentDerivation && (
                            <div className='rounded-3xl border border-border bg-card p-5'>
                                <p className='text-sm font-semibold text-muted-foreground'>{t({ hi: 'वर्तमान अङ्ग (लट्, लङ्, लोट्, विधिलिङ्)', en: 'Present stem (लट्, लङ्, लोट्, विधिलिङ्)' })}</p>
                                <p lang='sa' className='deva mt-2 text-2xl font-semibold text-ink'>{d.presentDerivation.expr}</p>
                                <p className='mt-2 text-sm text-muted-foreground'>{t(d.presentDerivation.note)}</p>
                            </div>
                        )}
                        {d.futureDerivation && (
                            <div className='rounded-3xl border border-border bg-card p-5'>
                                <p className='text-sm font-semibold text-muted-foreground'>{t({ hi: 'भविष्यत् अङ्ग (लृट्, लृङ्)', en: 'Future stem (लृट्, लृङ्)' })}</p>
                                <p lang='sa' className='deva mt-2 text-2xl font-semibold text-ink'>{d.futureDerivation.expr} → {d.future?.[0]}</p>
                                <p className='mt-2 text-sm text-muted-foreground'>{t(d.futureDerivation.note)}</p>
                            </div>
                        )}
                    </section>
                )}

                {d.notes.length > 0 && (
                    <section className='mt-6 rounded-3xl border-2 border-dashed border-saffron/50 bg-saffron-soft/40 p-5'>
                        <p className='font-semibold'>{t({ hi: 'विशेष बातें और अपवाद', en: 'Notes and irregularities' })}</p>
                        <ul className='mt-2 list-disc space-y-1 pl-5 text-sm text-foreground/90'>
                            {d.notes.map((n, i) => <li key={i}>{t(n)}</li>)}
                        </ul>
                    </section>
                )}

                {padasOf(d).map(pada => (
                    <section key={pada} className='mt-10'>
                        <h2 className='mb-3 font-heading text-2xl'>
                            <span lang='sa' className='deva'>{PADA_LABEL[pada].sa}</span>
                            <span className='ml-2 text-base text-muted-foreground'>{t({ hi: PADA_LABEL[pada].hi, en: PADA_LABEL[pada].en })}</span>
                        </h2>
                        <div className='space-y-3'>
                            {availableLakaras(d.id, pada).map((l, i) => <ParadigmSection key={l} dhatuId={d.id} lakara={l} pada={pada} defaultOpen={i === 0} />)}
                        </div>
                        {availableLakaras(d.id, pada).length < 10 && (
                            <p className='mt-3 text-sm text-muted-foreground'>{t({ hi: 'शेष लकार इस धातु के लिए अभी तैयार किए जा रहे हैं; अंक वाले अभ्यास में केवल सत्यापित रूप आते हैं।', en: 'The remaining lakāras for this root are still being prepared; only verified forms ever appear in graded practice.' })}</p>
                        )}
                    </section>
                ))}

                {kridantas.length > 0 && (
                    <section className='mt-10'>
                        <h2 className='mb-3 font-heading text-2xl'>{t({ hi: 'कृदन्त रूप', en: 'Participles and verbal derivatives' })}</h2>
                        <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
                            {kridantas.map(k => {
                                const p = KRIDANTA_PRATYAYAS.find(x => x.id === k.pratyaya)
                                return (
                                    <div key={k.form} className='rounded-2xl border border-border bg-card p-3'>
                                        <div className='flex items-center justify-between gap-2'>
                                            <Skt text={k.form} className='text-xl font-semibold text-ink' />
                                            <span lang='sa' className='deva rounded-full bg-muted px-2 py-0.5 text-xs'>{p?.name}</span>
                                        </div>
                                        <p className='text-sm text-muted-foreground'>{t(k.meaning)}</p>
                                    </div>
                                )
                            })}
                        </div>
                    </section>
                )}

                {prefixed.length > 0 && (
                    <section className='mt-10'>
                        <h2 className='mb-3 font-heading text-2xl'>{t({ hi: 'उपसर्ग से बदलता अर्थ', en: 'How prefixes change the meaning' })}</h2>
                        <div className='grid gap-2 sm:grid-cols-2'>
                            {prefixed.map(u => {
                                const pd = getDhatu(u.dhatuId)
                                return (
                                    <Link key={u.dhatuId} href={`/sanskrit/grammar/dhatu/${u.dhatuId}`} className='flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3 hover:border-saffron'>
                                        <span>
                                            <span lang='sa' className='deva text-lg font-semibold'>{u.upasarga} + {d.root} → {pd?.citation}</span>
                                            <span className='block text-sm text-muted-foreground'>{t(u.meaning)}</span>
                                        </span>
                                        <span className='text-saffron-strong'>→</span>
                                    </Link>
                                )
                            })}
                        </div>
                    </section>
                )}

                <section className='mt-10'>
                    <h2 className='mb-3 font-heading text-2xl'>{t({ hi: 'उदाहरण', en: 'Examples' })}</h2>
                    <ul className='space-y-2'>
                        {d.examples.map(ex => (
                            <li key={ex.sa} className='rounded-2xl border border-border bg-card p-4'>
                                <div className='flex items-start justify-between gap-3'>
                                    <Skt text={ex.sa} className='text-2xl' iast='auto' />
                                    <AudioButton text={ex.sa} size='sm' />
                                </div>
                                <p className='mt-1 text-muted-foreground'>{t({ hi: ex.hi, en: ex.en })}</p>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className='mt-10 rounded-3xl bg-muted/60 p-5 text-sm'>
                    <p className='font-semibold'>{t({ hi: 'स्रोत', en: 'Sources' })}</p>
                    <ul className='mt-2 space-y-1 text-muted-foreground'>
                        {d.sources.map(s => <li key={s}>{SOURCES[s].author}, <span lang='sa' className='deva'>{SOURCES[s].title}</span>{SOURCES[s].year ? ` (${SOURCES[s].year})` : ''}</li>)}
                    </ul>
                </section>
            </div>
            </SanskritGate>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: ALL_DHATUS.map(d => ({ params: { root: d.id } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<DhatuPageProps> = async ({ params }) => {
    const root = String(params?.root ?? '')
    if (!getDhatu(root)) return { notFound: true }
    return { props: { root } }
}

export default DhatuPage
